import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { X, AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/api';
import { PopupField, PopupCoordinates } from './PopupField';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { API_SOURCE_PATH } from './constants';
import ViewOnGoogleMaps from './ViewOnGoogleMaps';
import { StopwatchIcon } from '@radix-ui/react-icons';

export interface TtmsPopupProps {
	longitude: number;
	latitude: number;
	properties: Record<string, string>;
	onClose: () => void;
}

interface TtmsData {
	id: number;
	city: string;
	state: string;
	ttmsZone: string;
	latitude: number;
	longitude: number;
}

const fetchTtmsData = async (ttmsId: string): Promise<TtmsData> => {
	const response = await apiClient.get(`${API_SOURCE_PATH}/ttms/${ttmsId}`);
	return response.data;
};

export function TtmsPopup({ longitude, latitude, properties, onClose }: TtmsPopupProps) {
	const {
		data: fetchedData,
		isLoading,
		error
	} = useQuery({
		queryKey: ['geomap', 'ttms', properties.id],
		queryFn: () => fetchTtmsData(properties.id),
		enabled: !!properties.id
	});

	const ttmsData: TtmsData = {
		id: Number(properties.id) || 0,
		city: properties.city || '',
		state: properties.state || '',
		ttmsZone: properties.ttmsZone || '',
		latitude: latitude,
		longitude: longitude,
		...(fetchedData || {})
	};

	return (
		<Card className="bg-white dark:bg-slate-900 border shadow-lg min-w-[260px] max-w-[350px]">
			<CardHeader className="pb-2">
				<div className="flex items-center justify-between">
					<CardTitle className="flex items-center gap-2 text-sm">
						<StopwatchIcon className="h-4 w-4" />
						Ttms Details
					</CardTitle>
					<Button aria-label="close" variant="ghost" size="icon" onClick={onClose} className="h-6 w-6">
						<X className="h-3 w-3" />
					</Button>
				</div>
			</CardHeader>
			<CardContent className="space-y-3">
				<PopupCoordinates longitude={longitude} latitude={latitude} />
				<PopupField label="ID" value={ttmsData.id.toString()} />
				<PopupField label="City" value={ttmsData.city} />
				<PopupField label="State" value={ttmsData.state} />
				<PopupField label="Ttms Zone" value={ttmsData.ttmsZone} />
				<ViewOnGoogleMaps latitude={latitude} longitude={longitude} />
				{isLoading && (
					<div className="space-y-2">
						<Skeleton className="h-4 w-full" />
						<Skeleton className="h-4 w-3/4" />
						<Skeleton className="h-4 w-1/2" />
					</div>
				)}
				{error && (
					<div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-xs">
						<AlertCircle className="h-3 w-3" />
						Failed to load additional data
					</div>
				)}
			</CardContent>
		</Card>
	);
}
